import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Input,
  notification,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
} from 'antd';
import classNames from 'classnames';
import Title from 'antd/es/typography/Title';
import locale from 'antd/es/date-picker/locale/th_TH';
import {
  IBuyerSearchInvoiceRes,
  IBuyerSearchInvoice,
  IBuyerSearchInvoiceReq,
  IBuyerAcceptInvoice,
} from '@/interfaces/buyer';
import dayjs from 'dayjs';
import { useState } from 'react';
import { formatDate, formatNumber } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  invoiceAcceptance,
  invoiceAcceptances,
  searchBuyerInvoice,
} from '@/adapters/buyer';
import { EBuyerInvoiceStatus } from '@/enums/buyer';
import {
  getBuyerInvoiceStatusColor,
  getBuyerInvoiceStatusName,
  getBuyerInvoiceStatusOptions,
} from '@/helpers/buyer';
import VendorSelector from '@/components/selector/vendor-selector';
import { IVendor } from '@/interfaces/vendor';
import { IServerInfoRes } from '@/interfaces/misc';
import { getServerInfo } from '@/adapters/misc';
import RejectModal from '@/components/modal/reject-modal';
import { getQueryParam } from '@/utilities/queryUtils';
import { jobProcess } from '@/adapters/job';

const { RangePicker } = DatePicker;

const _criteria: IBuyerSearchInvoiceReq = {
  docNo: '',
  dateFrom: dayjs().add(-1, 'month').startOf('month').format('YYYY-MM-DD'),
  dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  status: EBuyerInvoiceStatus.PENDING,
  pageStart: 1,
  pageSize: -1,
  vendor: null,
};

const InvoicePage: React.FC = () => {
  const job = getQueryParam('job');
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState<IBuyerSearchInvoiceReq>({
    ..._criteria,
  });

  const [selectedRowKey, setSelectedRowKey] = useState<IBuyerSearchInvoice[]>(
    []
  );

  const [reject, setReject] = useState<{
    open: boolean;
    item?: IBuyerSearchInvoice;
  }>({
    open: false,
  });

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  const onFinish: FormProps<IBuyerSearchInvoiceReq>['onFinish'] = (values) => {
    setCriteria({
      ...criteria,
      docNo: values.docNo,
      dateFrom: values.docDate?.[0].format('YYYY-MM-DD') || undefined,
      dateTo: values.docDate?.[1].format('YYYY-MM-DD') || undefined,
      vendor: values.vendor,
      status: values.status,
    });
  };

  const onClear = () => {
    form.setFieldsValue({
      ..._criteria,
      docDate: [dayjs(_criteria.dateFrom), dayjs(_criteria.dateTo)],
    });
    form.submit();
  };

  const columns: TableColumnsType<IBuyerSearchInvoice> = [
    {
      title: 'ลำดับ',
      dataIndex: '',
      width: 40,
      align: 'center',
      className: 'text-bold',
      render: (_, rec, i) => <div className="font-weight-bold">{i + 1}</div>,
    },
    {
      title: 'เลขที่เอกสาร',
      dataIndex: 'AR_INV_NBR',
      align: 'center',
      className: 'text-bold',
      width: 160,
    },
    {
      title: 'วันที่',
      dataIndex: 'AR_INV_DATE',
      align: 'center',
      className: 'text-bold',
      width: 160,
      render: (_) => formatDate(_),
    },
    {
      title: 'ประเภทเอกสาร',
      dataIndex: 'AR__CHR02',
      align: 'center',
      className: 'text-bold',
      width: 160,
    },
    {
      title: 'เจ้าหนี้',
      dataIndex: 'AR_VENDER',
      className: 'text-bold',
      align: 'center',
      width: 160,
    },
    {
      title: 'ชื่อเจ้าหนี้',
      dataIndex: 'AR_VENDER_NAME',
      className: 'text-bold',
    },
    {
      title: 'ยอดเงิน',
      dataIndex: 'AR_AMT',
      align: 'right',
      width: 160,
      className: 'text-bold text-primary',
      render: (_) => formatNumber(_, 2),
    },
    {
      title: 'เลขที่ PO',
      dataIndex: 'AR__CHR03',
      className: 'text-bold',
      width: 160,
      align: 'center',
    },
    {
      title: 'สถานะการรับ',
      dataIndex: 'AR_STATUS',
      align: 'center',
      width: 160,
      className: 'text-bold',
      render: (_) => (
        <div
          className={`${classNames(getBuyerInvoiceStatusColor(_))} rounded pt-2 pb-2`}
        >
          {getBuyerInvoiceStatusName(_)}
        </div>
      ),
    },
    {
      title: 'remark',
      dataIndex: 'AR_REMARK',
      className: 'text-bold',
      hidden: criteria.status !== 'RJCT',
    },
    {
      title: '',
      dataIndex: 'AR_STATUS',
      align: 'center',
      width: 160,
      className: 'text-bold',
      render: (_, rec) => (
        <button
          type="button"
          className="btn btn-danger"
          style={{ minWidth: 150 }}
          onClick={() =>
            setReject({
              open: true,
              item: rec,
            })
          }
        >
          {mutations.isPending ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span className="sr-only">Loading...</span>
            </>
          ) : (
            'ไม่รับเอกสาร '
          )}
        </button>
      ),
      hidden: criteria.status !== EBuyerInvoiceStatus.PENDING,
    },
  ];

  const rowSelection: TableProps<IBuyerSearchInvoice>['rowSelection'] = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: IBuyerSearchInvoice[]
    ) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        'selectedRows: ',
        selectedRows
      );
      setSelectedRowKey(selectedRows);
    },
    getCheckboxProps: (record: IBuyerSearchInvoice) => ({
      disabled: ['ACPT', 'RJCT'].includes(record.AR_STATUS),
    }),
    selectedRowKeys: selectedRowKey.map((c) => c.AR_INV_NBR) || [],
  };

  const { data: invoices, refetch } = useQuery<
    AxiosResponse<IBuyerSearchInvoiceRes>,
    Error
  >({
    queryKey: ['search-buyer-inquiry', criteria],
    queryFn: async () => await searchBuyerInvoice(criteria),
    // enabled: !!criteria.vendor,
  });

  const { data: serverInfo } = useQuery<AxiosResponse<IServerInfoRes>, Error>({
    queryKey: ['search-server-info', criteria],
    queryFn: async () => await getServerInfo(),
  });

  const mutations = useMutation({
    mutationFn: async (data: IBuyerAcceptInvoice) =>
      await invoiceAcceptance(data),
    mutationKey: ['invoice-acceptance'],
    onSuccess: (data) => {
      if (data.status === 200) {
        notification.success({
          message: 'รับเอกสารสำเร็จ',
        });
        refetch();
        setSelectedRowKey([]);
        setReject({
          open: false,
          item: undefined,
        });
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  const mutationGroups = useMutation({
    mutationFn: async (data: IBuyerAcceptInvoice[]) =>
      await invoiceAcceptances(data),
    mutationKey: ['invoice-acceptances'],
    onSuccess: (data) => {
      if (data.status === 200) {
        const response = data?.data;
        if (response?.data?.length) {
          response?.data?.map((c) => {
            return notification.success({
              message: c.refNo,
              description: 'รับเอกสารสำเร็จ',
            });
          });
        } else {
          response?.errors?.map((c) => {
            return notification.error({
              message: c.refNo,
              description: c?.errors?.errorMessage,
            });
          });
        }
        refetch();
        setSelectedRowKey([]);
        setReject({
          open: false,
          item: undefined,
        });
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onEntry(status?: 'ACPT' | 'RJCT'): void {
    const acceptanceReqs: IBuyerAcceptInvoice[] = selectedRowKey.map((c) => {
      return {
        acceptance: status || 'ACPT',
        messageId: c.AR_MSTR_MESSID,
        remark: '',
        refNo: c.AR_INV_NBR,
      };
    });
    mutationGroups.mutateAsync(acceptanceReqs);
    // mutations.mutateAsync({
    //   acceptance: status || 'ACPT',
    //   messageId: selectedRowKey[0].AR_MSTR_MESSID,
    //   remark: '',
    // });
  }

  const onReject = (remark: string) => {
    mutations.mutateAsync({
      acceptance: 'RJCT',
      messageId: reject?.item?.AR_MSTR_MESSID,
      remark: remark,
    });
  };

  return (
    <>
      <Flex justify="space-between" gap={2}>
        <div>
          <Title level={2} className="text-center">
            {serverInfo?.data?.data?.[0]?.SERVER_NAME}
          </Title>
        </div>
        <div>
          <Title
            level={2}
            className="text-center"
            onClick={() => (job === 'x12' ? jobProcess('invoice') : undefined)}
          >
            ค้นหาเอกสารับใบแจ้งหนี้
          </Title>
        </div>
      </Flex>
      <Card>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...criteria,
            docDate: [dayjs(criteria.dateFrom), dayjs(criteria.dateTo)],
          }}
          onFinish={onFinish}
          autoComplete="off"
          labelAlign={'left'}
        >
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item name="docNo" label="เลขที่เอกสาร">
                <Input placeholder="เลขที่เอกสาร" size="large" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name={'docDate'} label="วันที่เอกสาร">
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  locale={locale}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<IBuyerSearchInvoiceReq> name="vendor" label="เจ้าหนี้">
                <VendorSelector
                  handleSelectData={function (vendor: IVendor): void {
                    form.setFieldsValue({
                      vendor: vendor?.VDAD_ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<IBuyerSearchInvoiceReq> name="status" label="สถานะ">
                <Select
                  showSearch
                  placeholder="เลือกสถานะ"
                  optionFilterProp="label"
                  size="large"
                  defaultValue={criteria?.status}
                  options={[
                    {
                      value: '',
                      label: 'ทั้งหมด',
                    },
                    ...getBuyerInvoiceStatusOptions(),
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap={24} justify="center">
            <button
              disabled={!selectedRowKey.length || mutations.isPending}
              type="button"
              className="btn btn-success"
              style={{ minWidth: 150 }}
              onClick={() => onEntry()}
            >
              {mutations.isPending || mutationGroups.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'รับเอกสาร '
              )}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ minWidth: 150 }}
            >
              ค้นหา
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={{ minWidth: 150 }}
              onClick={onClear}
            >
              ยกเลิก
            </button>
          </Flex>
        </Form>
      </Card>
      <Card className="mt-3">
        <Table<IBuyerSearchInvoice>
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          columns={columns}
          dataSource={invoices?.data?.data || []}
          size="small"
          bordered
          rowKey="AR_INV_NBR"
          pagination={{
            total: invoices?.data?.data?.length || 0,
            showTotal: (total) => `${total} รายการ`,
            showSizeChanger: true,
            locale: { items_per_page: '/หน้า' },
          }}
        />
        <RejectModal
          open={reject.open}
          title={'ยืนการรับเอกสาร'}
          content={'ปฏิเสธการรับเอกสาร'}
          handleOk={function (err): void {
            onReject(err);
          }}
          handleCancel={function (): void {
            setReject({
              open: false,
              item: undefined,
            });
          }}
        />
      </Card>
    </>
  );
};

export default InvoicePage;
